import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  ArrowLeft, 
  FileCode, 
  ShoppingCart, 
  User, 
  Search, 
  Menu, 
  Gift, 
  Award
} from 'lucide-react'

const PerkswallEmbeddedDemo: React.FC = () => {
  const [showImplementation, setShowImplementation] = useState<boolean>(false)
  const [points] = useState<number>(750)
  const [cartItems] = useState<number>(3)

  // Implementation code snippets
  const scriptCode = `<script>
   window.AdpxConfig = {
    accountId: "YOUR_ACCOUNT_ID", // Replace with your account ID
    themeId: "featured-offers-theme",
    containerId: 'adpx_container'
   };
   window.AdpxUser = {
   // payload goes here
   };
   var adpx = document.createElement('script');adpx.type = 'text/javascript';
   adpx.async = true;
   adpx.src = 'https://cdn.adspostx.com/launcher.perkswall.js';
   document.getElementsByTagName('head')[0].appendChild(adpx);
</script>`;

  const containerCode = `<div id="adpx_container"></div>`;

  // No clipboard functionality needed

  // Load Perkswall demo
  useEffect(() => {
    // Remove any existing scripts first to avoid conflicts
    const existingScript = document.getElementById('perkswall-script');
    if (existingScript) {
      existingScript.remove();
    }
    
    // Configure Perkswall according to the exact steps provided
    // Cast to 'any' to bypass TypeScript errors with the original implementation
    (window as any).AdpxConfig = {
      accountId: "ffa59da09972e55e",
      themeId: "featured-offers-theme",
      containerId: 'adpx_container'
    };
    
    // Cast to 'any' to bypass TypeScript errors
    (window as any).AdpxUser = {
      // Minimal required payload
      customerType: 'default',
      pageType: 'demo'
    };
    
    // Create and inject the script
    const adpxScript = document.createElement('script');
    adpxScript.id = 'perkswall-script';
    adpxScript.type = 'text/javascript';
    adpxScript.async = true;
    adpxScript.src = 'https://cdn.adspostx.com/launcher.perkswall.js';
    document.getElementsByTagName('head')[0].appendChild(adpxScript);
    
    // Log for debugging
    console.log('Perkswall script initialized', {
      config: (window as any).AdpxConfig,
      user: (window as any).AdpxUser,
      containerExists: !!document.getElementById('adpx_container')
    });
    
    return () => {
      // Clean up when component unmounts
      const script = document.getElementById('perkswall-script');
      if (script && script.parentNode) {
        script.parentNode.removeChild(script);
      }
      
      // Remove global variables
      delete (window as any).AdpxConfig;
      delete (window as any).AdpxUser;
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* E-commerce Site Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        {/* Top navigation bar */}
        <div className="bg-[#233D71] text-white py-2 text-sm">
          <div className="container mx-auto px-4 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span>Free shipping on orders over $50</span>
              <span className="hidden sm:inline">|</span>
              <span className="hidden sm:inline">30-day returns</span>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="#" className="hover:underline hidden sm:inline">Help</Link>
              <Link to="#" className="hover:underline hidden sm:inline">Store Locator</Link>
              <Link to="#" className="hover:underline">Sign In</Link>
            </div>
          </div>
        </div>
        
        {/* Main header */}
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Back Button (only for demo purposes) */}
            <Link
              to="/perkswall"
              className="inline-flex items-center space-x-2 text-gray-500 hover:text-gray-700 transition-colors mr-4"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Perkswall</span>
            </Link>

            {/* Store Brand */}
            <div className="flex items-center">
              <h1 className="text-2xl sm:text-3xl font-bold">
                <span className="text-[#233D71]">Style</span>
                <span className="text-[#FE4215]">Studio</span>
              </h1>
            </div>
            
            {/* Navigation */}
            <div className="hidden md:flex items-center space-x-6 text-gray-700">
              <Link to="#" className="hover:text-[#FE4215] transition-colors">Women</Link>
              <Link to="#" className="hover:text-[#FE4215] transition-colors">Men</Link>
              <Link to="#" className="hover:text-[#FE4215] transition-colors">Kids</Link>
              <Link to="#" className="hover:text-[#FE4215] transition-colors">Sale</Link>
            </div>
            
            {/* User Tools */}
            <div className="flex items-center space-x-4">
              {/* Loyalty points display */}
              <div className="hidden sm:flex items-center bg-[#233D71]/10 rounded-full px-3 py-1 text-sm">
                <Award className="w-4 h-4 text-[#233D71] mr-1" />
                <span className="font-medium">{points} points</span>
              </div>
              
              <button className="text-gray-700 hover:text-[#FE4215] transition-colors">
                <Search className="w-5 h-5" />
              </button>
              <button className="text-gray-700 hover:text-[#FE4215] transition-colors">
                <User className="w-5 h-5" />
              </button>
              <button className="text-gray-700 hover:text-[#FE4215] transition-colors relative">
                <ShoppingCart className="w-5 h-5" />
                <span className="absolute -top-1 -right-1 bg-[#FE4215] text-white rounded-full w-4 h-4 text-xs flex items-center justify-center">
                  {cartItems}
                </span>
              </button>
              <button className="md:hidden text-gray-700 hover:text-[#FE4215] transition-colors">
                <Menu className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Categories navigation */}
        <div className="border-t border-gray-200 hidden md:block">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between py-2 text-sm">
              <div className="flex space-x-6">
                <Link to="#" className="text-gray-700 hover:text-[#FE4215] transition-colors py-1">New Arrivals</Link>
                <Link to="#" className="text-gray-700 hover:text-[#FE4215] transition-colors py-1">Clothing</Link>
                <Link to="#" className="text-gray-700 hover:text-[#FE4215] transition-colors py-1">Shoes</Link>
                <Link to="#" className="text-gray-700 hover:text-[#FE4215] transition-colors py-1">Accessories</Link>
                <Link to="#" className="text-gray-700 hover:text-[#FE4215] transition-colors py-1">Beauty</Link>
              </div>
              
              <Link to="#" className="text-[#FE4215] font-medium hover:underline flex items-center">
                <Gift className="w-4 h-4 mr-1" />
                <span>All Rewards</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {/* Page Banner */}
        <div className="bg-gradient-to-r from-[#233D71] to-[#374b71] text-white py-8 mb-8">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="mb-6 md:mb-0 text-center md:text-left">
                <h1 className="text-3xl sm:text-4xl font-bold mb-2">Your Exclusive Rewards</h1>
                <p className="text-white/80 max-w-2xl">
                  As a valued StyleStudio member, you've unlocked special perks and offers.
                </p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 flex items-center space-x-4">
                <div className="text-center">
                  <div className="text-4xl font-bold">{points}</div>
                  <div className="text-white/80 text-sm">Available Points</div>
                </div>
                <div className="h-12 w-px bg-white/20"></div>
                <div>
                  <button className="bg-white text-[#233D71] px-4 py-2 rounded-lg font-medium hover:bg-white/90 transition-colors">
                    Redeem Points
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Main Content Area with Perkswall integration */}
        <div className="container mx-auto px-4 pb-16">
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">
                Personalized Recommendations
              </h2>
              
              {/* Implementation toggle (only for demo purposes) */}
              <button 
                onClick={() => setShowImplementation(prev => !prev)}
                className="text-sm bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded-full text-gray-700 transition-colors"
              >
                {showImplementation ? 'Hide Code' : 'Show Implementation'}
              </button>
            </div>
            
            {/* Implementation info */}
            {showImplementation && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-4 bg-white border border-gray-200 rounded-lg p-4 overflow-hidden"
              >
                <h3 className="text-lg font-medium text-gray-800 mb-3">
                  Implementation Details
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center text-sm text-gray-700 mb-2">
                      <FileCode className="w-4 h-4 mr-2 text-gray-500" />
                      <span className="font-medium">Script Implementation</span>
                    </div>
                    <div className="bg-gray-800 text-gray-200 rounded-lg p-3 text-xs overflow-x-auto">
                      <pre className="whitespace-pre-wrap">
                        {scriptCode}
                      </pre>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex items-center text-sm text-gray-700 mb-2">
                      <FileCode className="w-4 h-4 mr-2 text-gray-500" />
                      <span className="font-medium">Container Implementation</span>
                    </div>
                    <div className="bg-gray-800 text-gray-200 rounded-lg p-3 text-xs overflow-x-auto">
                      <pre className="whitespace-pre-wrap">
                        {containerCode}
                      </pre>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
          
          {/* Perkswall Container */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {/* Perkswall container as specified in the documentation */}
            <div id="adpx_container" className="w-full min-h-[500px]"></div>
          </div>
        </div>
      </main>
      
      {/* E-commerce Footer */}
      <footer className="bg-[#233D71] text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">Shop</h3>
              <ul className="space-y-2 text-sm text-white/80">
                <li><Link to="#" className="hover:text-white">New Arrivals</Link></li>
                <li><Link to="#" className="hover:text-white">Women</Link></li>
                <li><Link to="#" className="hover:text-white">Men</Link></li>
                <li><Link to="#" className="hover:text-white">Kids</Link></li>
                <li><Link to="#" className="hover:text-white">Sale</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Customer Service</h3>
              <ul className="space-y-2 text-sm text-white/80">
                <li><Link to="#" className="hover:text-white">Contact Us</Link></li>
                <li><Link to="#" className="hover:text-white">FAQs</Link></li>
                <li><Link to="#" className="hover:text-white">Shipping & Returns</Link></li>
                <li><Link to="#" className="hover:text-white">Size Guide</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Rewards Program</h3>
              <ul className="space-y-2 text-sm text-white/80">
                <li><Link to="#" className="hover:text-white">How It Works</Link></li>
                <li><Link to="#" className="hover:text-white">Earn Points</Link></li>
                <li><Link to="#" className="hover:text-white">Redeem Rewards</Link></li>
                <li><Link to="#" className="hover:text-white">View Your Balance</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Stay Connected</h3>
              <p className="text-sm text-white/80 mb-4">
                Sign up for exclusive offers, updates and rewards.
              </p>
              <div className="flex">
                <input 
                  type="email" 
                  placeholder="Your email" 
                  className="bg-white/10 border-none rounded-l-lg px-3 py-2 text-sm flex-1 focus:outline-none focus:ring-1 focus:ring-white/50" 
                />
                <button className="bg-[#FE4215] text-white px-3 py-2 rounded-r-lg text-sm hover:bg-[#E53910] transition-colors">
                  Subscribe
                </button>
              </div>
            </div>
          </div>
          
          <div className="border-t border-white/20 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-white/60">
            <div className="mb-4 md:mb-0">
              © 2025 StyleStudio. All rights reserved.
            </div>
            <div className="flex space-x-4">
              <Link to="#" className="hover:text-white">Privacy Policy</Link>
              <Link to="#" className="hover:text-white">Terms & Conditions</Link>
              <Link to="#" className="hover:text-white">Accessibility</Link>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Implementation Code Dialog (only available in demo) */}
      <Link
        to="/perkswall"
        className="fixed bottom-4 left-4 flex items-center space-x-2 bg-[#233D71] text-white px-3 py-2 rounded-lg shadow-lg hover:bg-[#1A2B49] transition-colors text-sm"
      >
        <ArrowLeft className="w-4 h-4" />
        <span>Back to Perkswall</span>
      </Link>
    </div>
  )
}

// No longer need type definitions since we're using the iframe approach

export default PerkswallEmbeddedDemo